import React from "react";

/**
 * Stylized digital-infinity motif used as ambient background graphic across the site.
 *
 * Variants:
 *  - "outline": just the rainbow-stroke infinity (ambient, low-emphasis)
 *  - "wireframe": outline + dotted node grid (more "digital" feel)
 *  - "blur": soft gradient blob shaped like infinity (hero glow)
 *
 * Usage tip: place inside a `relative overflow-hidden` parent and pass
 * `className` to absolutely position + size the motif.
 */
export const InfinityMotif = ({
  variant = "outline",
  className = "",
  opacity = 0.18,
  ...rest
}) => {
  const stroke = "url(#infinity-rainbow)";

  return (
    <svg
      viewBox="0 0 320 140"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      className={`pointer-events-none ${className}`}
      style={{ opacity }}
      {...rest}
    >
      <defs>
        <linearGradient id="infinity-rainbow" x1="0%" y1="50%" x2="100%" y2="50%">
          <stop offset="0%" stopColor="#55a2d2" />
          <stop offset="35%" stopColor="#7e7ec8" />
          <stop offset="55%" stopColor="#af3fa6" />
          <stop offset="78%" stopColor="#c96f7a" />
          <stop offset="100%" stopColor="#dda632" />
        </linearGradient>
        <radialGradient id="infinity-glow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#af3fa6" stopOpacity="0.45" />
          <stop offset="55%" stopColor="#55a2d2" stopOpacity="0.22" />
          <stop offset="100%" stopColor="#070b14" stopOpacity="0" />
        </radialGradient>
      </defs>

      {variant === "blur" && (
        <>
          <ellipse cx="100" cy="70" rx="90" ry="60" fill="url(#infinity-glow)" />
          <ellipse cx="220" cy="70" rx="90" ry="60" fill="url(#infinity-glow)" />
        </>
      )}

      {/* Core infinity loop — drawn as two intertwined curves */}
      <g fill="none" stroke={stroke} strokeWidth="3" strokeLinecap="round">
        <path d="M30 70 C 30 30, 100 30, 160 70 S 290 110, 290 70 S 220 30, 160 70 S 30 110, 30 70 Z" />
      </g>

      {variant === "wireframe" && (
        <g fill={stroke} opacity="0.9">
          {/* Node accents along the curve */}
          <circle cx="60" cy="55" r="2.6" fill="#55a2d2" />
          <circle cx="100" cy="42" r="2.2" fill="#7e7ec8" />
          <circle cx="160" cy="70" r="3.2" fill="#af3fa6" />
          <circle cx="220" cy="98" r="2.2" fill="#c96f7a" />
          <circle cx="260" cy="85" r="2.6" fill="#dda632" />
          {/* Subtle dotted rail */}
          <g stroke="url(#infinity-rainbow)" strokeWidth="1" strokeDasharray="2 6" opacity="0.6">
            <path d="M30 85 C 30 45, 100 45, 160 85 S 290 125, 290 85" fill="none" />
          </g>
        </g>
      )}
    </svg>
  );
};

export default InfinityMotif;
